import {
    Control,
    controlsUtils,
    FabricObject,
    Transform,
    TransformActionHandler,
} from "fabric";
import {FABRIC_DEFAULTS} from "./fabric-defaults";
import {FloorPlanItem, SeatConfig, SeatSlot, SeatSlotWithPosition, SlotEdge, TableShape} from "../types";
import {TABLE_MIN_SIZE} from "./constants";

const TABLE_RESIZE = 'resizing';
const NON_BOOKABLE_OUTLINE_COLOR = 'rgba(211, 47, 47, 0.4)';
const NON_BOOKABLE_OUTLINE_GAP = 4;
const NON_BOOKABLE_OUTLINE_WIDTH = 2;
const NON_BOOKABLE_OUTLINE_DASH = [10, 6];
const NON_BOOKABLE_OUTLINE_RADIUS = 8;
const DEFAULT_CIRCLE_SEAT_LAYOUT = {
    ...FABRIC_DEFAULTS.table.seats.circle,
};

export const isTableBookable = (item?: FloorPlanItem | null): boolean =>
    item?.table?.resource?.meta?.bookableExternally === true;

const normalizeAngleDeg = (angleDeg: number): number => {
    const normalized = angleDeg % 360;
    return normalized < 0 ? normalized + 360 : normalized;
};

const angleDegToEdge = (angleDeg: number): SlotEdge => {
    const normalized = normalizeAngleDeg(angleDeg);
    if (normalized >= 315 || normalized < 45) return 'top';
    if (normalized < 135) return 'right';
    if (normalized < 225) return 'bottom';
    return 'left';
};

const buildCircleSeatAngles = (count: number, circle: SeatConfig['circle'] | undefined): number[] => {
    if (count <= 0) return [];

    const layout = {
        ...DEFAULT_CIRCLE_SEAT_LAYOUT,
        ...(circle ?? {}),
    };
    const startAngleDeg = normalizeAngleDeg(layout.startAngleDeg ?? DEFAULT_CIRCLE_SEAT_LAYOUT.startAngleDeg);
    const sweepAngleDeg = Math.max(0, layout.sweepAngleDeg ?? DEFAULT_CIRCLE_SEAT_LAYOUT.sweepAngleDeg);

    if (count === 1) return [startAngleDeg];
    if (sweepAngleDeg >= 360) {
        return Array.from({length: count}, (_, index) =>
            normalizeAngleDeg(startAngleDeg + (360 / count) * index)
        );
    }
    if (layout.includeEndpoints) {
        return Array.from({length: count}, (_, index) =>
            normalizeAngleDeg(startAngleDeg + (sweepAngleDeg / (count - 1)) * index)
        );
    }

    return Array.from({length: count}, (_, index) =>
        normalizeAngleDeg(startAngleDeg + (sweepAngleDeg / count) * (index + 0.5))
    );
};

const calculateCircleSlotPositions = (
    radius: number,
    seatOffset: number,
    slots: SeatSlot[],
    circle: SeatConfig['circle'] | undefined
): SeatSlotWithPosition[] => {
    const generatedAngles = buildCircleSeatAngles(slots.length, circle);

    return slots.map((slot, i) => {
        const angleDeg = normalizeAngleDeg(slot.angleDeg ?? generatedAngles[i] ?? DEFAULT_CIRCLE_SEAT_LAYOUT.startAngleDeg);
        const radians = (angleDeg * Math.PI) / 180;
        const x = (radius + seatOffset) * Math.sin(radians);
        const y = -(radius + seatOffset) * Math.cos(radians);

        return {...slot, angleDeg, edge: angleDegToEdge(angleDeg), x, y};
    });
};

const calculateRectSlotPositions = (
    width: number,
    height: number,
    seatOffset: number,
    edges: SeatConfig['edges'],
    slots: SeatSlot[]
): SeatSlotWithPosition[] => {
    const result: SeatSlotWithPosition[] = [];
    let position = 0;

    const slotsByEdge: Record<SlotEdge, SeatSlot[]> = {
        top: [],
        right: [],
        bottom: [],
        left: [],
    };
    slots.forEach(slot => slotsByEdge[slot.edge].push(slot));

    for (let i = 0; i < edges.top; i++) {
        const x = -width / 2 + (i + 1) * (width / (edges.top + 1));
        const y = -height / 2 - seatOffset;
        const slot = slotsByEdge.top[i] || {id: `slot_${position}`, edge: 'top', position, priority: position};
        result.push({...slot, x, y});
        position++;
    }

    for (let i = 0; i < edges.right; i++) {
        const x = width / 2 + seatOffset;
        const y = -height / 2 + (i + 1) * (height / (edges.right + 1));
        const slot = slotsByEdge.right[i] || {id: `slot_${position}`, edge: 'right', position, priority: position};
        result.push({...slot, x, y});
        position++;
    }

    for (let i = 0; i < edges.bottom; i++) {
        const x = -width / 2 + (i + 1) * (width / (edges.bottom + 1));
        const y = height / 2 + seatOffset;
        const slot = slotsByEdge.bottom[i] || {id: `slot_${position}`, edge: 'bottom', position, priority: position};
        result.push({...slot, x, y});
        position++;
    }

    for (let i = 0; i < edges.left; i++) {
        const x = -width / 2 - seatOffset;
        const y = -height / 2 + (i + 1) * (height / (edges.left + 1));
        const slot = slotsByEdge.left[i] || {id: `slot_${position}`, edge: 'left', position, priority: position};
        result.push({...slot, x, y});
        position++;
    }

    return result;
};

const calculateSlotPositions = (
    shape: TableShape,
    width: number,
    height: number,
    seatOffset: number,
    edges: SeatConfig['edges'],
    slots: SeatSlot[],
    circle?: SeatConfig['circle']
): SeatSlotWithPosition[] => {
    if (shape === 'circle') {
        return calculateCircleSlotPositions(Math.min(width, height) / 2, seatOffset, slots, circle);
    }
    return calculateRectSlotPositions(width, height, seatOffset, edges, slots);
};

// Table resize updates width/height directly, so the control handlers must keep
// the opposite edge anchored instead of relying on Fabric scale normalization later.
const isCenteredTransform = (transform: Transform) =>
    transform.originX === 'center' && transform.originY === 'center';

const respectsOrigin = (value: number, origin: string | number | undefined) => {
    if (origin === 'center') return true;
    if (origin === 'left' || origin === 'top') return value >= 0;
    return value <= 0;
};

const getStrokePadding = (target: FabricObject, axis: 'x' | 'y') => {
    const scale = axis === 'x' ? (target.scaleX ?? 1) : (target.scaleY ?? 1);
    return (target.strokeWidth ?? 0) / ((target.strokeUniform ? scale : 1) || 1);
};

const getLocalPoint = (transform: Transform, x: number, y: number) =>
    controlsUtils.getLocalPoint(transform, transform.originX, transform.originY, x, y);

const wrapResizeHandler = (handler: TransformActionHandler) =>
    controlsUtils.wrapWithFireEvent(TABLE_RESIZE, controlsUtils.wrapWithFixedAnchor(handler));

const updateTableDimensions = (target: FloorplanTableObject, width: number, height: number) => {
    const nextWidth = Math.min(Math.max(width, target.getMinimumWidth()), target.getMaximumWidth());
    const nextHeight = Math.min(Math.max(height, target.getMinimumHeight()), target.getMaximumHeight());
    const shape = target.getTableShape();
    const finalWidth = shape === 'circle' ? Math.max(nextWidth, nextHeight) : nextWidth;
    const finalHeight = shape === 'circle' ? finalWidth : nextHeight;

    target.set({
        width: finalWidth,
        height: finalHeight,
        scaleX: 1,
        scaleY: 1,
    });
    target.syncFloorPlanItemCanvas();
    target.setCoords();
    return true;
};

const changeTableWidth: TransformActionHandler = (_eventData, transform, x, y) => {
    const target = transform.target as FloorplanTableObject;
    const localPoint = getLocalPoint(transform, x, y);
    if (!respectsOrigin(localPoint.x, transform.originX)) return false;

    const multiplier = isCenteredTransform(transform) ? 2 : 1;
    const width = Math.abs((localPoint.x * multiplier) / (target.scaleX ?? 1)) - getStrokePadding(target, 'x');
    return updateTableDimensions(target, width, target.height ?? target.getMinimumHeight());
};

const changeTableHeight: TransformActionHandler = (_eventData, transform, x, y) => {
    const target = transform.target as FloorplanTableObject;
    const localPoint = getLocalPoint(transform, x, y);
    if (!respectsOrigin(localPoint.y, transform.originY)) return false;

    const multiplier = isCenteredTransform(transform) ? 2 : 1;
    const height = Math.abs((localPoint.y * multiplier) / (target.scaleY ?? 1)) - getStrokePadding(target, 'y');
    return updateTableDimensions(target, target.width ?? target.getMinimumWidth(), height);
};

const changeTableCorner: TransformActionHandler = (_eventData, transform, x, y) => {
    const target = transform.target as FloorplanTableObject;
    const localPoint = getLocalPoint(transform, x, y);
    if (!respectsOrigin(localPoint.x, transform.originX) || !respectsOrigin(localPoint.y, transform.originY)) {
        return false;
    }

    const multiplier = isCenteredTransform(transform) ? 2 : 1;
    const width = Math.abs((localPoint.x * multiplier) / (target.scaleX ?? 1)) - getStrokePadding(target, 'x');
    const height = Math.abs((localPoint.y * multiplier) / (target.scaleY ?? 1)) - getStrokePadding(target, 'y');
    return updateTableDimensions(target, width, height);
};

const createTableControls = () => {
    const {mtr: _mtr, ...controls} = controlsUtils.createObjectDefaultControls();
    const resizeWidth = wrapResizeHandler(changeTableWidth);
    const resizeHeight = wrapResizeHandler(changeTableHeight);
    const resizeCorner = wrapResizeHandler(changeTableCorner);
    const defaultCursor = () => 'default';

    controls.ml = new Control({
        x: -0.5,
        y: 0,
        actionHandler: resizeWidth,
        cursorStyleHandler: defaultCursor,
        actionName: TABLE_RESIZE,
        render: controlsUtils.renderCircleControl,
    });
    controls.mr = new Control({
        x: 0.5,
        y: 0,
        actionHandler: resizeWidth,
        cursorStyleHandler: defaultCursor,
        actionName: TABLE_RESIZE,
        render: controlsUtils.renderCircleControl,
    });
    controls.mt = new Control({
        x: 0,
        y: -0.5,
        actionHandler: resizeHeight,
        cursorStyleHandler: defaultCursor,
        actionName: TABLE_RESIZE,
        render: controlsUtils.renderCircleControl,
    });
    controls.mb = new Control({
        x: 0,
        y: 0.5,
        actionHandler: resizeHeight,
        cursorStyleHandler: defaultCursor,
        actionName: TABLE_RESIZE,
        render: controlsUtils.renderCircleControl,
    });
    controls.tl = new Control({
        x: -0.5,
        y: -0.5,
        actionHandler: resizeCorner,
        cursorStyleHandler: defaultCursor,
        actionName: TABLE_RESIZE,
        render: controlsUtils.renderCircleControl,
    });
    controls.tr = new Control({
        x: 0.5,
        y: -0.5,
        actionHandler: resizeCorner,
        cursorStyleHandler: defaultCursor,
        actionName: TABLE_RESIZE,
        render: controlsUtils.renderCircleControl,
    });
    controls.bl = new Control({
        x: -0.5,
        y: 0.5,
        actionHandler: resizeCorner,
        cursorStyleHandler: defaultCursor,
        actionName: TABLE_RESIZE,
        render: controlsUtils.renderCircleControl,
    });
    controls.br = new Control({
        x: 0.5,
        y: 0.5,
        actionHandler: resizeCorner,
        cursorStyleHandler: defaultCursor,
        actionName: TABLE_RESIZE,
        render: controlsUtils.renderCircleControl,
    });
    return controls;
};

export class FloorplanTableObject extends FabricObject {
    static type = 'FloorplanTable';

    floorPlanItem?: FloorPlanItem;
    tableHideLabel = false;
    showSeatsAroundTable = true;
    occupiedSeatCount = 0;
    bodyFillOverride?: string;
    bodyStrokeOverride?: string;
    seatFillOverride?: string;
    seatStrokeOverride?: string;

    static customProperties = ['floorPlanItem', 'tableHideLabel', 'showSeatsAroundTable', 'occupiedSeatCount'];

    constructor(item: FloorPlanItem, showSeatsAroundTable = true, hideLabel = false, overrideSelectable?: boolean) {
        super();
        this.tableHideLabel = hideLabel;
        this.showSeatsAroundTable = showSeatsAroundTable;
        this.occupiedSeatCount = hideLabel ? 0 : Number.MAX_SAFE_INTEGER;
        this.objectCaching = false;
        this.strokeUniform = true;
        this.controls = createTableControls();
        this.applyFloorPlanItem(item, showSeatsAroundTable, hideLabel, overrideSelectable);
    }

    getTableShape(): TableShape {
        return this.floorPlanItem?.table?.shape ?? 'rectangle';
    }

    getMinimumWidth() {
        return TABLE_MIN_SIZE;
    }

    getMinimumHeight() {
        return TABLE_MIN_SIZE;
    }

    getMaximumWidth() {
        const canvasWidth = this.canvas?.getWidth?.() ?? 1000;
        return Math.max(canvasWidth * 4, 2000);
    }

    getMaximumHeight() {
        const canvasHeight = this.canvas?.getHeight?.() ?? 1000;
        return Math.max(canvasHeight * 4, 2000);
    }

    // Keep the persisted table canvas in sync with the live Fabric object so
    // sidebars, undo, and save all read the same geometry.
    syncFloorPlanItemCanvas() {
        if (!this.floorPlanItem?.table) return;
        // Persist the live Fabric geometry back onto the floorplan item so sidebars and save use the same dimensions.
        this.floorPlanItem = {
            ...this.floorPlanItem,
            table: {
                ...this.floorPlanItem.table,
                canvas: {
                    ...this.floorPlanItem.table.canvas,
                    width: this.width ?? this.getMinimumWidth(),
                    height: this.height ?? this.getMinimumHeight(),
                    left: this.left ?? 0,
                    top: this.top ?? 0,
                    angle: this.angle ?? 0,
                    scaleX: 1,
                    scaleY: 1,
                },
            },
        };
    }

    applyFloorPlanItem(
        item: FloorPlanItem,
        showSeatsAroundTable = this.showSeatsAroundTable,
        hideLabel = this.tableHideLabel,
        overrideSelectable?: boolean
    ) {
        const table = item.table;
        if (!table) {
            throw new Error('FloorplanTableObject requires item.table');
        }

        const canvas = table.canvas;
        const selectable = overrideSelectable ?? (canvas.selectable !== false);
        const width = table.shape === 'circle'
            ? Math.max(canvas.width ?? TABLE_MIN_SIZE, this.getMinimumWidth())
            : Math.max(canvas.width ?? FABRIC_DEFAULTS.table.width, this.getMinimumWidth());
        const height = table.shape === 'circle'
            ? width
            : Math.max(canvas.height ?? FABRIC_DEFAULTS.table.height, this.getMinimumHeight());

        this.floorPlanItem = {
            ...item,
            table: {
                ...table,
                canvas: {
                    ...canvas,
                    width,
                    height,
                    left: canvas.left ?? 0,
                    top: canvas.top ?? 0,
                    angle: canvas.angle ?? 0,
                    scaleX: 1,
                    scaleY: 1,
                },
            },
        };
        this.showSeatsAroundTable = showSeatsAroundTable;
        this.tableHideLabel = hideLabel;
        this.set({
            left: canvas.left ?? 0,
            top: canvas.top ?? 0,
            angle: canvas.angle ?? 0,
            width,
            height,
            opacity: 1,
            selectable,
            evented: selectable,
            hasControls: selectable,
            hasBorders: selectable,
            lockMovementX: !selectable,
            lockMovementY: !selectable,
            lockScalingX: false,
            lockScalingY: false,
            lockScalingFlip: true,
            hoverCursor: selectable ? 'move' : 'default',
            cornerColor: '#4285f4',
            cornerStrokeColor: '#ffffff',
            cornerSize: 12,
            transparentCorners: false,
            borderColor: '#4285f4',
            borderScaleFactor: 2,
            padding: 4,
            scaleX: 1,
            scaleY: 1,
            strokeWidth: (canvas.strokeWidth ?? FABRIC_DEFAULTS.table.strokeWidth) as number,
            stroke: (canvas.stroke ?? FABRIC_DEFAULTS.table.stroke) as string,
            fill: (canvas.fill ?? FABRIC_DEFAULTS.table.fill) as string,
        });
        this.setControlsVisibility({
            ml: table.shape !== 'circle',
            mr: table.shape !== 'circle',
            mt: table.shape !== 'circle',
            mb: table.shape !== 'circle',
        });
        this.setCoords();
    }

    setOccupiedSeatCount(count: number) {
        this.occupiedSeatCount = Math.max(0, count);
        this.dirty = true;
    }

    setBodyAppearance(fill?: string, stroke?: string) {
        this.bodyFillOverride = fill;
        this.bodyStrokeOverride = stroke;
        this.dirty = true;
    }

    clearBodyAppearance() {
        this.bodyFillOverride = undefined;
        this.bodyStrokeOverride = undefined;
        this.dirty = true;
    }

    setSeatAppearance(fill?: string, stroke?: string) {
        // Read mode uses this to tint seats by reservation status without mutating the stored table config.
        this.seatFillOverride = fill;
        this.seatStrokeOverride = stroke;
        this.dirty = true;
    }

    clearSeatAppearance() {
        this.seatFillOverride = undefined;
        this.seatStrokeOverride = undefined;
        this.dirty = true;
    }

    _render(ctx: CanvasRenderingContext2D) {
        const table = this.floorPlanItem?.table;
        if (!table) return;

        const canvas = table.canvas;
        const width = this.width ?? canvas.width ?? FABRIC_DEFAULTS.table.width;
        const height = this.height ?? canvas.height ?? FABRIC_DEFAULTS.table.height;
        const fill = this.bodyFillOverride
            ?? (canvas.fill ?? FABRIC_DEFAULTS.table.fill) as string;
        const stroke = this.bodyStrokeOverride
            ?? (canvas.stroke ?? FABRIC_DEFAULTS.table.stroke) as string;
        const strokeWidth = (canvas.strokeWidth ?? FABRIC_DEFAULTS.table.strokeWidth) as number;
        const strokeDashArray = canvas.strokeDashArray as number[] | undefined;

        // Seats are derived from the current body dimensions on every render,
        // so table resize never has to scale seat graphics first and "repair" them later.
        const seats: SeatConfig = table.seats ?? {
            size: FABRIC_DEFAULTS.table.seats.size,
            offset: FABRIC_DEFAULTS.table.seats.offset,
            edges: FABRIC_DEFAULTS.table.seats.edges,
            slots: [],
        };
        // Recompute seat positions from the current body size so resize/zoom never leaves stale seat geometry behind.
        const slots = calculateSlotPositions(table.shape, width, height, seats.offset, seats.edges, seats.slots, seats.circle);

        if (!isTableBookable(this.floorPlanItem)) {
            const visibleSlots = this.showSeatsAroundTable ? slots : [];
            const minX = Math.min(-width / 2, ...visibleSlots.map(slot => slot.x - seats.size));
            const minY = Math.min(-height / 2, ...visibleSlots.map(slot => slot.y - seats.size));
            const maxX = Math.max(width / 2, ...visibleSlots.map(slot => slot.x + seats.size));
            const maxY = Math.max(height / 2, ...visibleSlots.map(slot => slot.y + seats.size));
            const outlineX = minX - NON_BOOKABLE_OUTLINE_GAP;
            const outlineY = minY - NON_BOOKABLE_OUTLINE_GAP;
            const outlineWidth = maxX - minX + NON_BOOKABLE_OUTLINE_GAP * 2;
            const outlineHeight = maxY - minY + NON_BOOKABLE_OUTLINE_GAP * 2;
            const radius = Math.min(
                NON_BOOKABLE_OUTLINE_RADIUS,
                outlineWidth / 2,
                outlineHeight / 2,
            );

            ctx.save();
            ctx.beginPath();
            ctx.moveTo(outlineX + radius, outlineY);
            ctx.lineTo(outlineX + outlineWidth - radius, outlineY);
            ctx.quadraticCurveTo(
                outlineX + outlineWidth,
                outlineY,
                outlineX + outlineWidth,
                outlineY + radius,
            );
            ctx.lineTo(outlineX + outlineWidth, outlineY + outlineHeight - radius);
            ctx.quadraticCurveTo(
                outlineX + outlineWidth,
                outlineY + outlineHeight,
                outlineX + outlineWidth - radius,
                outlineY + outlineHeight,
            );
            ctx.lineTo(outlineX + radius, outlineY + outlineHeight);
            ctx.quadraticCurveTo(
                outlineX,
                outlineY + outlineHeight,
                outlineX,
                outlineY + outlineHeight - radius,
            );
            ctx.lineTo(outlineX, outlineY + radius);
            ctx.quadraticCurveTo(outlineX, outlineY, outlineX + radius, outlineY);
            ctx.closePath();
            ctx.strokeStyle = NON_BOOKABLE_OUTLINE_COLOR;
            ctx.lineWidth = NON_BOOKABLE_OUTLINE_WIDTH;
            ctx.setLineDash(NON_BOOKABLE_OUTLINE_DASH);
            ctx.stroke();
            ctx.restore();
        }

        ctx.save();
        ctx.beginPath();
        if (table.shape === 'circle') {
            ctx.arc(0, 0, Math.min(width, height) / 2, 0, Math.PI * 2);
        } else {
            const x = -width / 2;
            const y = -height / 2;
            const rx = Math.min(((canvas as any).rx ?? FABRIC_DEFAULTS.table.cornerRadius) as number, width / 2);
            const ry = Math.min(((canvas as any).ry ?? FABRIC_DEFAULTS.table.cornerRadius) as number, height / 2);
            ctx.moveTo(x + rx, y);
            ctx.lineTo(x + width - rx, y);
            ctx.quadraticCurveTo(x + width, y, x + width, y + ry);
            ctx.lineTo(x + width, y + height - ry);
            ctx.quadraticCurveTo(x + width, y + height, x + width - rx, y + height);
            ctx.lineTo(x + rx, y + height);
            ctx.quadraticCurveTo(x, y + height, x, y + height - ry);
            ctx.lineTo(x, y + ry);
            ctx.quadraticCurveTo(x, y, x + rx, y);
        }
        ctx.closePath();
        ctx.fillStyle = fill;
        ctx.fill();

        if (strokeWidth > 0 && stroke !== 'transparent') {
            ctx.save();
            ctx.lineWidth = strokeWidth;
            ctx.strokeStyle = stroke;
            ctx.setLineDash(strokeDashArray ?? []);
            ctx.stroke();
            ctx.restore();
        }
        ctx.restore();

        if (!this.tableHideLabel) {
            ctx.save();
            ctx.fillStyle = canvas.textColor ?? FABRIC_DEFAULTS.table.textColor;
            ctx.font = 'bold 12px sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            const name = table.resource?.name ?? 'Table';
            ctx.fillText(name, 0, -8);
            ctx.fillText(`(${slots.length})`, 0, 8);
            ctx.restore();
        }

        if (!this.showSeatsAroundTable) {
            return;
        }

        const sortedSlots = [...slots].sort((a, b) => (a.priority ?? a.position) - (b.priority ?? b.position));
        sortedSlots.forEach((slot, index) => {
            // Hidden-label tables use occupiedSeatCount to show only the seats currently taken; visible-label tables show all seats.
            const isActive = this.tableHideLabel ? index < this.occupiedSeatCount : true;
            const seatFill = isActive
                ? (this.seatFillOverride ?? FABRIC_DEFAULTS.seat.fillActive)
                : FABRIC_DEFAULTS.seat.fillInactive;
            const seatStroke = isActive
                ? (this.seatStrokeOverride ?? FABRIC_DEFAULTS.seat.strokeActive)
                : FABRIC_DEFAULTS.seat.strokeInactive;
            ctx.save();
            ctx.beginPath();
            ctx.arc(slot.x, slot.y, seats.size, 0, Math.PI * 2);
            ctx.fillStyle = seatFill;
            ctx.strokeStyle = seatStroke;
            ctx.lineWidth = 1.5;
            ctx.fill();
            ctx.stroke();
            ctx.restore();

            if (!this.tableHideLabel) {
                ctx.save();
                ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
                ctx.font = '10px sans-serif';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(String((slot.priority ?? index) + 1), slot.x, slot.y);
                ctx.restore();
            }
        });
    }
}

export const isFloorplanTableObject = (obj: FabricObject | undefined | null): obj is FloorplanTableObject =>
    !!obj && !!obj.floorPlanItem?.table;
